import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row, Skeleton } from 'antd'
import classnames from 'classnames'

import getBenefitSummaryApi, {
  PlanPeriodSummary,
} from 'core/application/employeeDashboard/summary/benefitSummary/getSummary'

import Card from 'common/components/dataDisplay/Card'
import Icon from 'common/components/general/Icon'

import { queryRequest } from 'common/RequestUtils'
import { parseZoneFormat } from 'common/dateFormatters'

import AvailableAllowanceSummary from './availableAllowanceSummary'
import CostSharingPercentageSummary from './costSharingPercentageSummary'
import HraDeductibleSummary from './hraDeductibleSummary'

import './BenefitSummary.less'

const BenefitSummary: React.FC = () => {
  const { t: translate } = useTranslation('ee-dashboard')
  const [benefitSummary, setBenefitSummary] = useState<PlanPeriodSummary[]>([])
  const [loadingSummary, setLoadingSummary] = useState<boolean>(false)

  useEffect(() => {
    getBenefitSummary()
  }, [])

  const getBenefitSummary = async () => {
    setLoadingSummary(true)
    const benefit = await queryRequest(() => getBenefitSummaryApi())
    setBenefitSummary(benefit !== undefined ? benefit : [])
    setLoadingSummary(false)
  }

  return (
    <Card title={translate('summary.benefit-summary-title')}>
      <Skeleton active loading={loadingSummary}>
        {benefitSummary.map((planPeriodSummary: PlanPeriodSummary, index: number) => {
          return (
            <React.Fragment key={planPeriodSummary.planPeriodId}>
              {index > 0 ? <div className="plan_period_horizontal_line mt-24 mb-24"></div> : null}
              <div className="benefit_summary" key={index}>
                <div className="pb-8 plan_period_interval">
                  <Icon type="far fa-calendar-alt" fontAwesome className="mr-4" />
                  <h6>
                    {translate('summary.plan-year-label')}: {parseZoneFormat(planPeriodSummary.startDate)} -{' '}
                    {parseZoneFormat(planPeriodSummary.endDate)}
                  </h6>
                </div>
                <AvailableAllowanceSummary planPeriodSummary={planPeriodSummary} translate={translate} />
                <Row>
                  {planPeriodSummary.hraDeductible !== null ? (
                    <Col className="pt-16" span={12}>
                      <HraDeductibleSummary
                        amount={planPeriodSummary.hraDeductible.deductibleAmount}
                        accomplishedAmount={planPeriodSummary.hraDeductible.accomplishedDeductibleAmount}
                        accomplishedPercentage={planPeriodSummary.hraDeductible.accomplishedDeductiblePercentage}
                        translate={translate}
                      />
                    </Col>
                  ) : null}
                  {planPeriodSummary.costSharingPercentage !== null ? (
                    <Col
                      className={classnames({
                        'pt-16': true,
                        'pl-24': planPeriodSummary.hraDeductible !== null,
                      })}
                      span={planPeriodSummary.hraDeductible !== null ? 12 : 24}
                    >
                      <CostSharingPercentageSummary
                        percentage={planPeriodSummary.costSharingPercentage}
                        hasDeductible={planPeriodSummary.hraDeductible !== null}
                        translate={translate}
                      />
                    </Col>
                  ) : null}
                </Row>
              </div>
            </React.Fragment>
          )
        })}
      </Skeleton>
    </Card>
  )
}

export default BenefitSummary
